import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaShoppingCart } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import "./CartDrawer.css";
import CommonButton from "../../button/CommonButton";
import CartProductDrawer from "./CartProductDrawer";
import { appApiHandler } from "../../../api/appApiHandler";
import { setCartCount } from "../../../store/slices/countState";
import EmptyState from "../../EmptyState";
import { showErrorToast } from "../../../utils/toast";
import { getErrorMessage, getPrice } from "../../../constants/globalFunction";
import { string } from "../../../constants/string";
import { colors } from "../../../constants/colors";

const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [products, setProducts] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subTotal, setSubTotal] = useState(0);

  const getCartProducts = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getCart(reqData);
    if (response?.status === 200 && response?.data) {
      dispatch(setCartCount(response?.data?.products?.length));
      setProducts(response?.data?.products);
    }

    setLoading(false);
    setSelectedItem(null);
  };

  useEffect(() => {
    if (isOpen && isLoggedIn && userData?._id) {
      setLoading(true);
      getCartProducts();
    }
  }, [isOpen, isLoggedIn, userData?._id]);

  useEffect(() => {
    const calculateTotalAmount = async () => {
      const itemTotals = await Promise.all(
        products?.map(async (item) => {
          const price = await getPrice(item?.size);
          return price * item?.qty;
        })
      );
      const total = itemTotals.reduce((acc, curr) => acc + curr, 0);
      setSubTotal(total);
    };
    calculateTotalAmount();
  }, [products]);

  const handleIncreaseQty = async (item) => {
    setSelectedItem(item);
    const reqData = {
      user_id: userData?._id,
      cart_id: item?.cart_id,
      product_id: item?._id,
      size_id: item?.size_id,
      qty: item?.qty + 1,
      color_id: item?.color_id,
    };
    setLoading(true);
    try {
      const response = await appApiHandler.addToCart(reqData);
      if (response?.status === 200) {
        getCartProducts();
      } else if (response?.message) {
        setLoading(false);
        setSelectedItem(null);
        showErrorToast(getErrorMessage(response?.message));
      } else {
        setLoading(false);
        setSelectedItem(null);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleUpdateQty = async (item, qty) => {
    setSelectedItem(item);
    const reqData = {
      user_id: userData?._id,
      cart_id: item?.cart_id,
      product_id: item?._id,
      size_id: item?.size_id,
      qty: qty,
      color_id: item?.color_id,
    };
    setLoading(true);
    try {
      const response = await appApiHandler.addToCart(reqData);
      if (response?.status === 200) {
        getCartProducts();
      } else if (response?.message) {
        getCartProducts();
        setSelectedItem(null);
        showErrorToast(getErrorMessage(response?.message));
      } else {
        getCartProducts();
        setSelectedItem(null);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (error) {
      getCartProducts();
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const handleDecreaseQty = async (item, deleteItem = false) => {
    setSelectedItem(item);
    const reqData = {
      user_id: userData?._id,
      product_id: item?._id,
      size_id: item?.size_id,
      qty: deleteItem || item?.qty <= 1 ? 0 : item?.qty - 1,
      color_id: item?.color_id,
    };
    setLoading(true);
    try {
      const response = await appApiHandler.removeFromCart(reqData);
      if (response?.status === 200) {
        getCartProducts();
      } else if (response?.message) {
        setLoading(false);
        setSelectedItem(null);
        showErrorToast(getErrorMessage(response?.message));
      } else {
        setLoading(false);
        setSelectedItem(null);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const onPressCheckout = () => {
    const hasUnavailable = products.some((p) => p?.is_update);
    if (hasUnavailable) {
      showErrorToast("Please remove unavailable items");
      return;
    }
    onClose();
    navigate("/address");
  };


  const renderProducts = useCallback(
    (item) => {
      return (
        <CartProductDrawer
          key={item?._id}
          item={item}
          onIncreaseQty={handleIncreaseQty}
          onDecreaseQty={handleDecreaseQty}
          onUpdateQty={handleUpdateQty}
          selectedItem={selectedItem}
        />
      );
    },
    [products, selectedItem]
  );

  if (!isOpen) return null;

  return (
    <div className="cartDrawerOverlay" onClick={onClose}>
      <div className="cartDrawer" onClick={(e) => e.stopPropagation()}>
        <div className="cartDrawerHeader">
          <h3>SHOPPING CART</h3>
          <button className="cartDrawerClose" onClick={onClose}>
            <IoClose size={23} color={colors.primary} />
          </button>
        </div>

        <div className="cartDrawerContent">
          {!isLoggedIn ? (
            <div className="cartDrawerLoginWrapper">
              <EmptyState
                icon={FaShoppingCart}
                title="Sign In to Continue"
                description="Please login with your account to view your cart and continue shopping!"
                buttonText="Login"
                onButtonClick={() => {
                  onClose();
                  navigate("/login");
                }}
                iconSize={48}
                style={{
                  border: 'none',
                  background: 'transparent',
                  margin: '0',
                  padding: '0'
                }}
              />
            </div>
          ) : products?.length > 0 ? (
            <>
              <div className="cartDrawerList">
                {products?.map((item) => renderProducts(item))}
              </div>
              <div className="cartDrawerFooter">
                <div className="cartDrawerSubtotal">
                  <span className="cartDrawerSubtotalLabel">Subtotal:</span>
                  <span className="cartDrawerSubtotalAmount">
                    ₹{subTotal}
                  </span>
                </div>
                <CommonButton
                  title="Checkout"
                  onPress={onPressCheckout}
                  customClassName="cartDrawerCheckoutBtn"
                />
              </div>
            </>
          ) : (
            <div className="cartDrawerEmptyState">
              <div className="cartDrawerEmptyIcon">
                <FaShoppingCart size={80} color="var(--color-grey)" />
              </div>
              <h3 className="cartDrawerEmptyTitle">Your cart is empty.</h3>
              <CommonButton
                title="RETURN TO SHOP"
                onPress={() => {
                  onClose();
                  navigate("/");
                }}
                customClassName="cartDrawerReturnBtn"
              />
            </div>
          )}
          {loading && (
            <div className="cartDrawerLoadingOverlay">
              <div className="cartDrawerLoadingSpinner"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
